import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { useClearGuidedArrival } from "../guidedAction";

/**
 * v4.2.1 — Guided Arrival Banner.
 *
 * Rendered at the top of any destination page when the operator
 * arrived via a guided action (`?guided_from=analytics`).
 *
 * The banner makes the operator's context legible:
 *   * what suggestion brought them here
 *   * what evidence triggered it
 *   * what the system is suggesting they consider
 *   * an explicit "Nothing has changed yet" reassurance
 *
 * The banner is calm + dismissible. Dismissing only clears the
 * arrival markers — pre-fill state stays intact so the operator can
 * keep working.
 */
export const GuidedArrivalBanner = ({
    title,
    explanation,
    evidence,
    onDismiss,
    testId = "ob-guided-arrival",
}) => {
    const { t } = useTranslation();
    const clearGuided = useClearGuidedArrival();
    const handleDismiss = () => {
        clearGuided();
        if (typeof onDismiss === "function") onDismiss();
    };
    return (
        <section
            role="region"
            aria-label={t("ob.guided.banner_label")}
            data-testid={testId}
            className="mb-8 rounded-2xl px-5 py-4 sm:px-6 sm:py-5"
            style={{
                background: "rgba(16,185,129,0.07)",
                border: "1px solid rgba(16,185,129,0.22)",
            }}
        >
            <div className="flex items-start gap-3">
                <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center flex-shrink-0 rounded-full w-7 h-7 mt-0.5"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16,185,129,0.12)",
                    }}
                >
                    <Sparkles size={14} strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                    <p
                        className="text-xs uppercase tracking-[0.18em] mb-1"
                        style={{
                            color: "#A7F3D0",
                            fontFamily: "Outfit, sans-serif",
                        }}
                        data-testid={`${testId}-eyebrow`}
                    >
                        {t("ob.guided.eyebrow")}
                    </p>
                    <p
                        className="text-base mb-1.5"
                        style={{
                            color: "var(--ob-text-primary)",
                            fontFamily: "Outfit, sans-serif",
                        }}
                        data-testid={`${testId}-title`}
                    >
                        {title || t("ob.guided.default_title")}
                    </p>
                    {explanation && (
                        <p
                            className="text-sm leading-relaxed mb-2"
                            style={{ color: "var(--ob-text-secondary)" }}
                            data-testid={`${testId}-explanation`}
                        >
                            {explanation}
                        </p>
                    )}
                    <p
                        className="text-xs"
                        style={{ color: "var(--ob-text-tertiary)" }}
                        data-testid={`${testId}-reassurance`}
                    >
                        {t("ob.guided.reassurance")}
                    </p>
                    {Array.isArray(evidence) && evidence.length > 0 && (
                        <details className="mt-3 group">
                            <summary
                                className="text-xs cursor-pointer select-none"
                                style={{ color: "var(--ob-text-tertiary)" }}
                            >
                                {t("ob.guided.evidence_label")}
                            </summary>
                            <ul className="mt-2 space-y-1">
                                {evidence.map((line, idx) => (
                                    <li
                                        key={idx}
                                        className="text-xs leading-relaxed"
                                        style={{ color: "var(--ob-text-secondary)" }}
                                    >
                                        · {line}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label={t("ob.guided.dismiss")}
                    data-testid={`${testId}-dismiss`}
                    className="flex-shrink-0 inline-flex items-center justify-center rounded-full p-1.5 hover:bg-white/5 transition"
                    style={{ color: "var(--ob-text-tertiary)" }}
                >
                    <X size={14} strokeWidth={1.5} />
                </button>
            </div>
        </section>
    );
};

export default GuidedArrivalBanner;
